import {
	AutoPlacementOptions,
	arrow,
	autoPlacement,
	autoUpdate,
	FloatingArrow,
	flip,
	offset,
	shift,
	useFloating,
} from "@floating-ui/react";
import { createContext, ReactNode, useRef, useState } from "react";

export const PopoverWithCaratContext = createContext({ close: () => {} });

export const PopoverWithCarat = ({
	buttonComponent,
	children,
	anchorLocation = ["bottom"],
	defaultOpen = false,
	isOpen: controlledIsOpen,
	onToggle: controlledOnToggle,
}: {
	buttonComponent: ReactNode; // Button is for display only and should not have it's own `onClick` handler
	children: ReactNode;
	anchorLocation?: AutoPlacementOptions["allowedPlacements"];
	defaultOpen?: boolean;
	isOpen?: boolean;
	onToggle?: () => void;
}) => {
	const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

	const isControlled = controlledIsOpen !== undefined;
	const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
	const toggleOpen = isControlled ? controlledOnToggle : () => setInternalIsOpen((prev) => !prev);
	const arrowRef = useRef(null);
	const { refs, floatingStyles, context } = useFloating({
		transform: true,
		middleware: [
			offset(16),
			flip(),
			shift({
				mainAxis: true,
				boundary: "clippingAncestors",
				padding: 12,
			}),
			autoPlacement({
				allowedPlacements: anchorLocation,
				autoAlignment: true,
				boundary: "clippingAncestors",
				padding: 12,
			}),
			arrow({
				element: arrowRef,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const closeHandler = () => {
		if (isControlled) {
			controlledOnToggle?.();
		} else {
			setInternalIsOpen(false);
		}
	};

	return (
		<>
			<div ref={refs.setReference} onClick={toggleOpen}>
				{buttonComponent}
			</div>
			{isOpen && (
				<div ref={refs.setFloating} style={floatingStyles} className="z-[100]">
					<div className="relative bg-white border-gray-200 border rounded-md shadow-[0px_2px_4px_rgba(0,0,0,0.12)]">
						<PopoverWithCaratContext.Provider value={{ close: closeHandler }}>
							{children}
						</PopoverWithCaratContext.Provider>
						<FloatingArrow
							ref={arrowRef}
							context={context}
							className="fill-white [&>path:first-of-type]:stroke-gray-200 [&>path:last-of-type]:stroke-white"
							strokeWidth={1}
							width={15}
							height={12.5}
						/>
					</div>
				</div>
			)}
		</>
	);
};
